/**
 * WhatsApp Business API conversation mocks.
 *
 * Per-order, per-factory threads. Messages have:
 *   - sender: 'us' | 'them'
 *   - body, ts, status (sent/delivered/read), translated (auto-translated badge)
 *   - kind: 'text' | 'template' | 'image' | 'voice' | 'doc'
 *
 * Multi-channel: each conversation flags whatsapp/email/alibaba so the UI
 * can show channel badges. WhatsApp is the primary demo channel.
 */

const t = (daysAgo, hour, min) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hour, min, 0, 0);
  return d.toISOString();
};

export const CONVERSATIONS = {
  // ---- Tactical handheld radios — outreach phase ----
  'demo-radios': {
    'f1': {
      factoryId: 'f1',
      channels: ['whatsapp', 'email', 'alibaba'],
      contactName: 'Mei Lin',
      contactPhone: '+86 138 1234 5678',
      lastSeen: t(0, 9, 14),
      online: true,
      messages: [
        { id: 'm1', sender: 'us',   ts: t(5, 9, 30),  status: 'read',      kind: 'template', body: 'Hello — RFQ for 350 IP67 handheld radios. Specs attached.' },
        { id: 'm2', sender: 'us',   ts: t(5, 9, 31),  status: 'read',      kind: 'doc',      body: 'tech-spec-radios-v1.pdf', meta: { size: 410_000 } },
        { id: 'm3', sender: 'them', ts: t(5, 14, 22), status: 'delivered', kind: 'text',     body: 'Hi! Yes, we manufacture this exact model. Sending quote tomorrow.', translatedFrom: 'zh-CN', original: '你好！我们就是生产这个型号。明天发报价。' },
        { id: 'm4', sender: 'them', ts: t(4, 11, 8),  status: 'delivered', kind: 'doc',      body: 'topsky-quote-Q25841.pdf', meta: { size: 280_000 }, translatedFrom: 'zh-CN' },
        { id: 'm5', sender: 'them', ts: t(4, 11, 9),  status: 'delivered', kind: 'text',     body: 'Our quote: $138/unit FOB Shenzhen. Lead time 28 days. ISED + IP67 + MIL-STD-810 all certified. Ask if you need certificates.', translatedFrom: 'zh-CN' },
        { id: 'm6', sender: 'us',   ts: t(3, 10, 0),  status: 'read',      kind: 'text',     body: 'Thanks Mei. Can you share the MIL-STD-810 test report?' },
        { id: 'm7', sender: 'them', ts: t(3, 13, 45), status: 'delivered', kind: 'doc',      body: 'milstd810h-report-T25-PRO.pdf', meta: { size: 2_100_000 }, translatedFrom: 'zh-CN' },
        { id: 'm8', sender: 'them', ts: t(3, 13, 46), status: 'delivered', kind: 'text',     body: 'Tested by SGS Hong Kong, Aug 2025. Drop, vibration, salt fog all passed.', translatedFrom: 'zh-CN' },
        { id: 'm9', sender: 'us',   ts: t(0, 9, 12),  status: 'sent',      kind: 'text',     body: 'One more — can you do $130/unit at 350 qty? We have other quotes at that level.' },
      ],
    },
    'f2': {
      factoryId: 'f2',
      channels: ['whatsapp', 'alibaba'],
      contactName: 'James Wu',
      contactPhone: '+86 159 8721 4499',
      lastSeen: t(2, 16, 30),
      online: false,
      messages: [
        { id: 'm1', sender: 'us',   ts: t(5, 9, 30), status: 'read',      kind: 'template', body: 'Hello — RFQ for 350 IP67 handheld radios. Specs attached.' },
        { id: 'm2', sender: 'them', ts: t(4, 8, 0),  status: 'delivered', kind: 'text',     body: 'Quote: $124/unit EXW Quanzhou. Lead time 35 days. ISED + IP67 done, MIL-STD-810 not certified for this model.', translatedFrom: 'zh-CN' },
        { id: 'm3', sender: 'us',   ts: t(4, 9, 0),  status: 'read',      kind: 'text',     body: 'Without MIL-STD-810 we can\'t bid this — RCMP requires it. Can you sub it via testing?' },
        { id: 'm4', sender: 'them', ts: t(2, 16, 30), status: 'delivered', kind: 'text',    body: 'Testing would add 6 weeks and $4,200. Probably not viable for this timeline.', translatedFrom: 'zh-CN' },
      ],
    },
    'f4': {
      factoryId: 'f4',
      channels: ['whatsapp', 'email'],
      contactName: 'Tanaka-san',
      contactPhone: '+81 90 8421 7733',
      lastSeen: t(1, 10, 0),
      online: false,
      messages: [
        { id: 'm1', sender: 'us',   ts: t(5, 9, 30), status: 'read',      kind: 'template', body: 'Hello — RFQ for 350 IP67 handheld radios.' },
        { id: 'm2', sender: 'them', ts: t(4, 7, 0),  status: 'delivered', kind: 'text',     body: 'Vertex Standard quote: $198/unit FOB Yokohama, 21-day lead time. Premium build, RCMP-grade.' },
        { id: 'm3', sender: 'them', ts: t(4, 7, 1),  status: 'delivered', kind: 'doc',      body: 'vertex-quote-RFQ-7882.pdf', meta: { size: 380_000 } },
      ],
    },
  },

  // ---- DND furniture — production phase ----
  'demo-furniture': {
    'g1': {
      factoryId: 'g1',
      channels: ['whatsapp', 'alibaba'],
      contactName: 'Wei Zhang',
      contactPhone: '+86 137 9988 1122',
      lastSeen: t(0, 14, 30),
      online: true,
      messages: [
        { id: 'm1',  sender: 'us',   ts: t(20, 9, 0),   status: 'read',      kind: 'template', body: 'PO confirmed. Production start?' },
        { id: 'm2',  sender: 'them', ts: t(20, 14, 0),  status: 'delivered', kind: 'text',     body: 'Production starting Day 15. We confirm BIFMA + GREENGUARD certs in package.', translatedFrom: 'zh-CN' },
        { id: 'm3',  sender: 'them', ts: t(15, 11, 30), status: 'delivered', kind: 'image',    body: 'Frame welding line — Day 7 of production', meta: { url: 'production-week1' }, translatedFrom: 'zh-CN' },
        { id: 'm4',  sender: 'them', ts: t(8, 14, 0),   status: 'delivered', kind: 'image',    body: 'Worksurface QC, 60% complete on first shipment', meta: { url: 'production-week2' }, translatedFrom: 'zh-CN' },
        { id: 'm5',  sender: 'us',   ts: t(7, 10, 0),   status: 'read',      kind: 'text',     body: 'Looks great. ETA on first batch?' },
        { id: 'm6',  sender: 'them', ts: t(7, 15, 22),  status: 'delivered', kind: 'text',     body: 'Day 30 ready for QC inspection. Day 32 packed for pickup.', translatedFrom: 'zh-CN' },
        { id: 'm7',  sender: 'them', ts: t(2, 9, 0),    status: 'delivered', kind: 'image',    body: 'Day 20 update — task chairs on assembly line', meta: { url: 'production-week3' }, translatedFrom: 'zh-CN' },
        { id: 'm8',  sender: 'them', ts: t(0, 14, 30),  status: 'delivered', kind: 'text',     body: '60% done on workstations, chairs in QC. On track for Day 32 pickup.', translatedFrom: 'zh-CN' },
      ],
    },
  },

  // ---- LED streetlights — invoicing phase ----
  'demo-streetlights': {
    'l1': {
      factoryId: 'l1',
      channels: ['whatsapp', 'email'],
      contactName: 'Li Wei',
      contactPhone: '+86 130 5566 7788',
      lastSeen: t(5, 12, 0),
      online: false,
      messages: [
        { id: 'm1', sender: 'us',   ts: t(45, 8, 0),  status: 'read', kind: 'template', body: 'PO confirmed for 1100 streetlights' },
        { id: 'm2', sender: 'them', ts: t(40, 9, 0),  status: 'read', kind: 'text',     body: 'Production complete day 35. Photos attached.', translatedFrom: 'zh-CN' },
        { id: 'm3', sender: 'them', ts: t(15, 8, 30), status: 'read', kind: 'text',     body: 'Container loaded and at port. Tracking shared with freighter.', translatedFrom: 'zh-CN' },
        { id: 'm4', sender: 'us',   ts: t(0, 13, 0),  status: 'read', kind: 'text',     body: 'Goods delivered — POD attached. Closing out this contract. Thanks Li!' },
      ],
    },
  },
};

export const channelLabel = (c) => ({
  whatsapp: 'WhatsApp',
  email:    'Email',
  alibaba:  'Alibaba IM',
}[c] || c);

export const getConversation = (orderId, factoryId) =>
  CONVERSATIONS[orderId]?.[factoryId] || null;
